import { useState } from 'react';
import { Modal } from './Modal';

export const PRINT_LAYOUTS = ['numbers', 'cards', 'list', 'detailed'] as const;
export type PrintLayout = (typeof PRINT_LAYOUTS)[number];

const DESCRIPTIONS: Record<PrintLayout, string> = {
  numbers: 'Big numbers and first names, nothing else — for the door or the wall.',
  cards: 'Photo grid — good for a sub folder or a seating reference.',
  list: 'One line per student — numbers, names and flags only.',
  detailed: 'Names with their About text and most recent note.',
};

const LABELS: Record<PrintLayout, string> = {
  numbers: 'Numbers and names',
  cards: 'Cards',
  list: 'List',
  detailed: 'Detailed',
};

/** Choose what a printed roster looks like before sending it to the printer. */
export function PrintRosterMenu({
  current,
  privacyOn,
  onPrint,
  onClose,
}: {
  current: PrintLayout;
  privacyOn: boolean;
  onPrint: (layout: PrintLayout) => void;
  onClose: () => void;
}) {
  const [layout, setLayout] = useState<PrintLayout>(current);

  return (
    <Modal
      onClose={onClose}
      label="Print the roster"
      backdrop="bg-espresso/30 print:hidden"
      className="w-full max-w-sm rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-1 text-lg">Print the roster</h2>
      <p className="mb-4 text-xs text-stone">Pick a layout. Archived students are left out.</p>

      <ul className="mb-4 space-y-1.5">
        {PRINT_LAYOUTS.map((l) => (
          <li key={l}>
            <button
              onClick={() => setLayout(l)}
              aria-pressed={layout === l}
              className={[
                'w-full rounded-card border px-3 py-2 text-left transition-colors',
                layout === l ? 'border-terracotta bg-linen/60' : 'border-sand hover:bg-linen/40',
              ].join(' ')}
            >
              <span className="block text-sm text-espresso">{LABELS[l]}</span>
              <span className="block text-[11px] text-stone">{DESCRIPTIONS[l]}</span>
            </button>
          </li>
        ))}
      </ul>

      {privacyOn && layout !== 'numbers' && (
        <p className="mb-3 rounded-lg bg-ochre/20 px-3 py-2 text-xs text-walnut">
          The privacy screen is on, so photos will print blurred and names as initials. Turn it off
          first if this is a copy for yourself.
        </p>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="btn-secondary">
          Cancel
        </button>
        <button onClick={() => onPrint(layout)} className="btn-primary">
          Print
        </button>
      </div>
    </Modal>
  );
}
